import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, ArrowUpRight, ExternalLink, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { StatusBadge } from './StatusBadge';

const SOCIAL_PLATFORMS = {
  instagram: { label: 'Instagram', prefix: 'https://instagram.com/', color: 'text-pink-600' },
  tiktok: { label: 'TikTok', prefix: 'https://tiktok.com/@', color: 'text-stone-800' },
  twitter: { label: 'X / Twitter', prefix: 'https://x.com/', color: 'text-sky-500' },
  facebook: { label: 'Facebook', prefix: 'https://facebook.com/', color: 'text-blue-600' },
};

export function ProviderDetailModal({ provider, onClose, onProviderUpdate }) {
  // Local tier state so the toggle reacts instantly without a full refetch.
  const initialTier = (provider?.subscription_tier || 'free').toLowerCase();
  const [tier, setTier] = useState(initialTier);
  const [updatingTier, setUpdatingTier] = useState(false);

  const handleToggleTier = async () => {
    if (!provider) return;
    const nextTier = tier === 'pro' ? 'free' : 'pro';
    setUpdatingTier(true);
    try {
      const res = await api.put(
        `/admin/providers/${provider.provider_id}/subscription-tier`,
        { tier: nextTier },
      );
      setTier(res.data.current_tier);
      toast.success(
        nextTier === 'pro'
          ? `${provider.brand_name} is now on Pro`
          : `${provider.brand_name} reverted to Free`,
      );
      // Let the parent refresh its list row so the badge stays in sync.
      if (onProviderUpdate) {
        onProviderUpdate({ ...provider, subscription_tier: res.data.current_tier });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change tier');
    } finally {
      setUpdatingTier(false);
    }
  };

  return (
    <Dialog open={!!provider} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Provider Details</DialogTitle>
        </DialogHeader>
        {provider && (
          <div className="space-y-4 pt-2" data-testid="provider-detail-modal">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#D4AF37] flex items-center justify-center text-white text-xl font-heading overflow-hidden">
                {provider.profile_image
                  ? <img src={provider.profile_image} alt="" className="w-full h-full object-cover" />
                  : provider.brand_name?.charAt(0)}
              </div>
              <div>
                <h3 className="font-heading font-medium text-lg">{provider.brand_name}</h3>
                <p className="text-sm text-stone-500">@{provider.username}, {provider.user_email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={provider.status} />
                  {provider.flagged && <span className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Flagged</span>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[#F9F6F0] p-3 rounded-lg"><span className="text-stone-400 block text-xs">Location</span>{provider.location || 'N/A'}</div>
              <div className="bg-[#F9F6F0] p-3 rounded-lg"><span className="text-stone-400 block text-xs">Rating</span>{provider.rating > 0 ? `${provider.rating.toFixed(1)} (${provider.review_count} reviews)` : 'No reviews'}</div>
              <div className="bg-[#F9F6F0] p-3 rounded-lg">
                <span className="text-stone-400 block text-xs mb-1">Tier</span>
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                      tier === 'pro'
                        ? 'bg-[#D4AF37]/15 text-[#8B7500]'
                        : 'bg-stone-200 text-stone-600'
                    }`}
                    data-testid="provider-tier-badge"
                  >
                    {tier === 'pro' && <Sparkles className="h-3 w-3" />}
                    {tier === 'pro' ? 'Pro' : 'Free'}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleToggleTier}
                    disabled={updatingTier}
                    className="h-7 text-xs px-2"
                    data-testid="toggle-provider-tier"
                  >
                    {updatingTier ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      tier === 'pro' ? 'Set Free' : 'Set Pro'
                    )}
                  </Button>
                </div>
              </div>
              <div className="bg-[#F9F6F0] p-3 rounded-lg"><span className="text-stone-400 block text-xs">Badge</span>{provider.badge || 'None'}</div>
            </div>
            {provider.bio && (
              <div><p className="text-xs text-stone-400 mb-1">Bio</p><p className="text-sm text-stone-600">{provider.bio}</p></div>
            )}
            {provider.categories && provider.categories.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {provider.categories.map(c => (
                  <span key={c} className="text-xs px-2 py-0.5 bg-[#F9F6F0] text-stone-500 rounded capitalize">{c}</span>
                ))}
              </div>
            )}
            {provider.social_links && Object.entries(provider.social_links).some(([, v]) => v && v.trim()) && (
              <div data-testid="provider-social-links">
                <p className="text-xs text-stone-400 mb-2">Social Links</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(provider.social_links)
                    .filter(([, v]) => v && v.trim())
                    .map(([platform, handle]) => {
                      const cfg = SOCIAL_PLATFORMS[platform] || { label: platform, prefix: '', color: 'text-stone-600' };
                      const url = handle.startsWith('http') ? handle : `${cfg.prefix}${handle.replace('@', '')}`;
                      return (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E4DD] text-sm hover:bg-[#F9F6F0] transition-colors ${cfg.color}`}
                          data-testid={`social-link-${platform}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          <span className="font-medium">{cfg.label}</span>
                          <span className="text-stone-400 text-xs truncate max-w-[120px]">@{handle.replace('@', '').replace(/https?:\/\/[^/]+\//, '')}</span>
                        </a>
                      );
                    })}
                </div>
              </div>
            )}
            <div className="pt-2 border-t border-[#E6E4DD]">
              <Link to={`/@${provider.username}`} target="_blank" className="inline-flex items-center gap-1 text-sm text-[#D4AF37] hover:underline">
                View public profile <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
